import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { AuthContext } from "@context-providers/auth/auth-context";
import { getUlmUrl, isLoginProviderFaro } from "@context-providers/auth/auth-utils";
import { AuthBroadcastChannel, useAuthBroadcastChannel } from "@context-providers/auth/use-auth-broadcast-channel";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useLoadingSpinner } from "@context-providers/loading-spinner-provider";
import { isUlmRedirectMessage } from "@faro-lotv/gate-keepers";
import { useRouteMatch, isAccountPageRoute } from "@hooks/use-route-match";
import { Dialog } from "@flat-ui";
import { runtimeConfig } from "@src/runtime-config";
import { HttpUtils } from "@stellar/utils";
import { resetStore } from "@store/store";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { setLoggedInUserFetchingStatus } from "@store/user/user-slice";
import { useCallback, useEffect, useState } from "react";

/**
 * Component that handles authentication actions with the Unified Login Mask (ULM):
 * - Checks the user login status.
 * - Checks if the user sessions has expired.
 * - Handles login and logout actions.
 * - TODO: Checks if the user has confirmed their email after registration: https://faro01.atlassian.net/browse/ST-2258
 * - TODO: Checks if the user information is complete: https://faro01.atlassian.net/browse/ST-1593.
 */
export function AuthProvider(t0) {
  const $ = _c(39);
  const {
    children
  } = t0;
  const [loginStatus, setLoginStatus] = useState("unknown");
  const [shouldShowSessionExpiredDialog, setShouldShowSessionExpiredDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [auth0LogoutUrl, setAuthLogout0Url] = useState(undefined);
  const {
    setLoadingSpinner
  } = useLoadingSpinner();
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast,
    handleErrorWithPage
  } = useErrorContext();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      logoutCallback: () => setShouldShowSessionExpiredDialog(true)
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const broadcastChannel = useAuthBroadcastChannel(t1);
  const routeIdentifier = useRouteMatch();
  let t2;
  if ($[1] !== routeIdentifier) {
    t2 = isAccountPageRoute(routeIdentifier);
    $[1] = routeIdentifier;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isAccountPage = t2;
  let t3;
  if ($[3] !== handleErrorWithPage || $[4] !== loggedInUser || $[5] !== setLoadingSpinner) {
    t3 = async () => {
      setLoadingSpinner(true);
      ;
      try {
        if (loggedInUser) {
          setLoginStatus("loggedIn");
        } else {
          await faroCoreApiClient.users.isLoggedIn();
          setLoginStatus("loggedIn");
        }
      } catch (t4) {
        const error = t4;
        const errorStatus = HttpUtils.getErrorStatus(error);
        if (errorStatus && errorStatus === HttpUtils.HTTP_ERROR_CODE.Unauthorized) {
          setLoginStatus("loggedOut");
        } else {
          handleErrorWithPage({
            id: `checkLoginStatus-${Date.now().toString()}`,
            title: "Failed to check the login status of the user. Please reload the page to try again.",
            error
          });
        }
        setLoadingSpinner(false);
      }
    };
    $[3] = handleErrorWithPage;
    $[4] = loggedInUser;
    $[5] = setLoadingSpinner;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const checkLogin = t3;
  let t4;
  if ($[7] !== broadcastChannel || $[8] !== dispatch || $[9] !== handleErrorWithToast || $[10] !== loggedInUser || $[11] !== setLoadingSpinner) {
    t4 = async () => {
      setIsLoggingOut(true);
      setLoadingSpinner(true);
      ;
      try {
        await faroCoreApiClient.users.logoutUser();
        if (isLoginProviderFaro(loggedInUser)) {
          const domain = runtimeConfig.features.auth0.domain;
          const clientId = runtimeConfig.features.auth0.clientId;
          const returnTo = encodeURIComponent(runtimeConfig.urls.sphereEntryPageUrl);
          const logoutUrl = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
          setAuthLogout0Url(logoutUrl);
        }
        setLoginStatus("loggedOut");
        resetStore(dispatch);
        dispatch(setLoggedInUserFetchingStatus("succeeded"));
        if (broadcastChannel) {
          broadcastChannel.postMessage(AuthBroadcastChannel.messages.logoutMessage);
        }
      } catch (t5) {
        const error_0 = t5;
        handleErrorWithToast({
          id: `logoutUser-${Date.now().toString()}`,
          title: "Failed to logout. Please try again or reload the page",
          error: error_0
        });
      }
      setIsLoggingOut(false);
      setLoadingSpinner(false);
    };
    $[7] = broadcastChannel;
    $[8] = dispatch;
    $[9] = handleErrorWithToast;
    $[10] = loggedInUser;
    $[11] = setLoadingSpinner;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const logout = t4;
  let t5;
  let t6;
  if ($[13] !== checkLogin || $[14] !== loginStatus) {
    t5 = () => {
      if (loginStatus === "unknown") {
        checkLogin();
      }
    };
    t6 = [checkLogin, loginStatus];
    $[13] = checkLogin;
    $[14] = loginStatus;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(_temp, t7);
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setShouldShowSessionExpiredDialog(true);
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== loginStatus || $[20] !== logout) {
    t9 = {
      loginStatus,
      showSessionExpiredDialog: t8,
      logout
    };
    $[19] = loginStatus;
    $[20] = logout;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      display: "none"
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== auth0LogoutUrl) {
    t11 = <iframe title="auth0-logout-iframe" style={t10} src={auth0LogoutUrl} />;
    $[23] = auth0LogoutUrl;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== isAccountPage || $[26] !== loginStatus) {
    t12 = loginStatus === "loggedOut" && !isAccountPage && <iframe title="unified-login-mask" id="data-testid-ULM-IFRAME" style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      border: "none"
    }} src={getUlmUrl()} />;
    $[25] = isAccountPage;
    $[26] = loginStatus;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== children || $[29] !== isAccountPage || $[30] !== isLoggingOut || $[31] !== loginStatus || $[32] !== shouldShowSessionExpiredDialog) {
    t13 = (loginStatus === "loggedIn" && !isLoggingOut || !!isAccountPage) && <div data-testid="auth-provider">{children}<Dialog open={shouldShowSessionExpiredDialog} title="Session expired" onConfirm={_temp2} confirmText="Login">Your session has expired. Please login again to continue.</Dialog></div>;
    $[28] = children;
    $[29] = isAccountPage;
    $[30] = isLoggingOut;
    $[31] = loginStatus;
    $[32] = shouldShowSessionExpiredDialog;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t11 || $[35] !== t12 || $[36] !== t13 || $[37] !== t9) {
    t14 = <AuthContext.Provider value={t9}>{t11}{t12}{t13}</AuthContext.Provider>;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}
function _temp2() {
  return window.location.reload();
}
function _temp() {
  const onWindowMessage = function onWindowMessage(message) {
    if (message?.data?.identifier === "UlmRedirectMessage" && isUlmRedirectMessage(message.data)) {
      window.location.href = message.data.redirectUrl;
    }
  };
  window.addEventListener("message", onWindowMessage);
  return () => window.removeEventListener("message", onWindowMessage);
}